// #1543: a page appears once, with its data, rather than as placeholders its data then replaces.
//
// When the console opens a page, the page area stays invisible while the page still shows loading
// placeholders, up to a short limit. Only the painting is held back: the page is mounted, reads,
// takes focus and restores its scroll position as usual. A page whose answers are remembered
// (#1536) has no placeholders and shows at once; a page slower than the limit shows its loading
// state after it, as before.
import React from "react";

/**
 * What a page shows while its answers are outstanding: design-system skeletons and regions marked
 * busy. A button busy with an action the operator started is not a page still loading.
 */
export const LOADING_PLACEHOLDER = '.neo-skel, [aria-busy="true"]:not(button)';

/** Set on the page area while it is held back; the stylesheet keeps it invisible. */
export const SETTLING_ATTRIBUTE = "data-page-settling";

/** How long a page opened from the console may stay invisible. */
export const NAVIGATION_SETTLE_MS = 800;

/** How long the loading screen may stay up after sign-in or a reload, waiting for the first page. */
export const SIGN_IN_SETTLE_MS = 2_500;

/**
 * Holds back painting `target` from the commit in which `pageKey` changes until it contains no
 * loading placeholder and `waiting` is false, or until the limit passes, then calls `onShown`.
 * Only placeholders present when the page is committed hold it back, so a page already showing
 * never disappears again while it refreshes. A null key holds nothing back.
 */
export function usePageSettle(
  target: React.RefObject<HTMLElement | null>,
  pageKey: string | null,
  options: { limitMs: number; waiting?: boolean; onShown?: () => void },
): void {
  // Read when the page key changes rather than as dependencies: a limit or a wait that changes
  // afterwards must not hide a page that is already showing.
  const latest = React.useRef(options);
  latest.current = options;
  const recheck = React.useRef<(() => void) | null>(null);

  React.useLayoutEffect(() => {
    if (pageKey === null) return;
    const element = target.current;
    const loading = () => latest.current.waiting === true
      || (element !== null && element.querySelector(LOADING_PLACEHOLDER) !== null);
    if (element === null || !loading()) {
      latest.current.onShown?.();
      return;
    }

    let finished = false;
    const observer = new MutationObserver(() => { if (!loading()) show(); });
    const timer = window.setTimeout(() => show(), latest.current.limitMs);
    const stop = () => {
      finished = true;
      observer.disconnect();
      window.clearTimeout(timer);
      element.removeAttribute(SETTLING_ATTRIBUTE);
      recheck.current = null;
    };
    const show = () => {
      if (finished) return;
      stop();
      latest.current.onShown?.();
    };
    element.setAttribute(SETTLING_ATTRIBUTE, "");
    observer.observe(element, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-busy"] });
    recheck.current = () => { if (!loading()) show(); };
    return () => { if (!finished) stop(); };
  }, [pageKey, target]);

  const waiting = options.waiting === true;
  React.useEffect(() => { recheck.current?.(); }, [waiting]);
}
